import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeGrid, GridChildComponentProps, areEqual } from "react-window";
import { NFTCardItem, NFTItemClickFunc } from "@/interfaces/nftTypes";
import NFTCard from "@/components/nft/NFTCard";
import useMediaQuery from "@/hooks/useMediaQuery";
import memoize from "memoize-one";
import { throttle } from "lodash";

const GUTTER_SIZE = 16;
const SAFE_DISTANCE = 50;
const FILTER_SIZE = 88;

interface NFTCardsWindowProps {
  list: NFTCardItem[];
  onItemClick?: NFTItemClickFunc;
  onScrollEnd?: () => void;
}

const Row = (props: GridChildComponentProps) => {
  const { rowIndex, columnIndex, style, data } = props;
  const index = columnIndex + data.columnCount * rowIndex;
  const cellData = data.list[index];
  if (!cellData) return null;
  return (
    <div
      key={cellData.id ? cellData.id : index}
      className="list-item"
      style={{
        ...style,
        left: (style.left as number) + GUTTER_SIZE,
        top: (style.top as number) + GUTTER_SIZE,
        width: (style.width as number) - GUTTER_SIZE,
        height: (style.height as number) - GUTTER_SIZE,
      }}
    >
      <NFTCard {...cellData} onCardClick={data.onItemClick} />
    </div>
  );
};

const MemoRow = React.memo(Row, areEqual);

const Inner = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  function Inner({ style, ...rest }, ref) {
    return (
      <div
        ref={ref}
        style={{
          ...style,
          marginBottom: FILTER_SIZE + "px", // 空出 Filter 组件位置
        }}
        {...rest}
      />
    );
  }
);

// This helper function memoizes incoming props,
// To avoid causing unnecessary re-renders pure Row components.
// This is only needed since we are passing multiple props with a wrapper object.
// If we were only passing a single, stable value (e.g. items),
// We could just pass the value directly.
const createItemData = memoize((list, columnCount, onItemClick) => ({
  list,
  columnCount,
  onItemClick,
}));
const NFTCardsWindow = ({
  list,
  onItemClick,
  onScrollEnd,
}: NFTCardsWindowProps) => {
  const S = useMediaQuery("(min-width:360px)");
  const M = useMediaQuery("(min-width:600px)");
  const MD = useMediaQuery("(min-width:1025px)");
  const LG = useMediaQuery("(min-width:1201px)");
  const XL = useMediaQuery("(min-width:1600px)");

  const grid = {
    cols: 0,
  };

  if (XL) {
    grid.cols = 8;
  } else if (LG) {
    grid.cols = 6;
  } else if (MD) {
    grid.cols = 5;
  } else if (M) {
    grid.cols = 4;
  } else if (S) {
    grid.cols = 2;
  } else {
    grid.cols = 1;
  }

  // Bundle additional data to list items using the "itemData" prop.
  // It will be accessible to item renderers as props.data.
  // Memoize this data to avoid bypassing shouldComponentUpdate().
  const itemData = createItemData(list, grid.cols, onItemClick);
  const columnCountMemo = useMemo(() => {
    if (list.length < grid.cols) {
      return list.length;
    }
    return grid.cols;
  }, [list, grid.cols]);

  const [innerRefHeight, setInnerRefHeight] = useState(0);
  const mountedCallBack = (el: HTMLElement) => {
    if (el && el.clientHeight) {
      setInnerRefHeight(el.clientHeight + FILTER_SIZE);
    }
  };

  const handleScroll = (
    scrollTop: number,
    height: number,
    innerRefHeight: number,
    onScrollEnd: Function
  ) => {
    if (scrollTop === 0 || height === 0) {
      return;
    }
    const scrollEnd = scrollTop + height + SAFE_DISTANCE >= innerRefHeight;
    if (scrollEnd) {
      onScrollEnd && onScrollEnd();
    }
  };

  const throttleScrollHandler = useCallback(
    throttle(
      (
        scrollTop: number,
        height: number,
        innerRefHeight: number,
        onScrollEnd: Function
      ) => handleScroll(scrollTop, height, innerRefHeight, onScrollEnd),
      300
    ),
    []
  );

  return (
    <AutoSizer>
      {({ height, width }: { height: number; width: number }) => {
        const columnWidth = width / grid.cols - 8;
        return (
          <FixedSizeGrid
            className="grid"
            height={height}
            width={width}
            columnCount={columnCountMemo}
            columnWidth={columnWidth}
            innerElementType={Inner}
            rowCount={Math.ceil(list.length / grid.cols)}
            rowHeight={columnWidth * 1.375}
            itemData={itemData}
            onScroll={(e) =>
              throttleScrollHandler(
                e.scrollTop,
                height,
                innerRefHeight,
                onScrollEnd as any
              )
            }
            innerRef={mountedCallBack}
          >
            {MemoRow}
          </FixedSizeGrid>
        );
      }}
    </AutoSizer>
  );
};

export default NFTCardsWindow;
